import { useEffect, useRef, useState } from 'react';
import { Modal } from './Modal';
import { FluxoRecarga } from '../paginas/painel/RecarregarPix';
import { reais } from '../util/formato';

/** Tempo que a confirmação fica visível antes do modal sair sozinho. */
const ESPERA_FECHAR_MS = 1_800;

/**
 * Recarga sem sair da tela.
 *
 * O problema que resolve: o usuário sobe 40 arquivos SPED, filtra 52 mil notas
 * e só então descobre que falta saldo. Mandá-lo para a tela de Créditos custa
 * todo esse trabalho — ao voltar, a análise continua lá, mas o filtro montado
 * não. Aqui ele paga por cima do que já fez e o botão de baixar destrava
 * sozinho, sem recarregar a página.
 *
 * `aoConfirmar` é chamado quando o Pix cai. Quem abriu o modal usa isso para
 * reconsultar o orçamento — é o servidor que decide se o saldo agora cobre, não
 * uma conta feita no navegador.
 */
export function ModalRecarga({
  aberto,
  aoFechar,
  aoConfirmar,
  /** Quanto falta para o lote, em centavos. Vira o valor preenchido. */
  faltamCentavos,
}: {
  aberto: boolean;
  aoFechar: () => void;
  aoConfirmar: () => void;
  faltamCentavos?: number;
}) {
  const [pago, setPago] = useState(false);
  const avisou = useRef(false);

  // Reabrir depois de um pagamento tem que começar do zero, senão o modal
  // abriria já mostrando "confirmado" do Pix anterior.
  useEffect(() => {
    if (aberto) {
      setPago(false);
      avisou.current = false;
    }
  }, [aberto]);

  useEffect(() => {
    if (!pago) return;
    // Fecha sozinho, mas só depois de a confirmação ser lida: sumir no
    // instante do pagamento deixa a dúvida de se deu certo.
    const t = window.setTimeout(aoFechar, ESPERA_FECHAR_MS);
    return () => clearTimeout(t);
  }, [pago, aoFechar]);

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Adicionar crédito"
      descricao={
        faltamCentavos !== undefined
          ? `Faltam ${reais(faltamCentavos)} para iniciar esta busca. Pague o Pix e o botão libera sozinho.`
          : 'Pague o Pix e o saldo entra na hora.'
      }
      largura={620}
    >
      {/* Monta só quando ABRE. Montado junto com a página, o fluxo pegaria
          `valorInicial` como `undefined` — o orçamento ainda não chegou nesse
          instante — e o campo travaria no valor padrão, porque `useState` só
          usa o argumento na primeira renderização. De quebra, evita consultar
          os limites de recarga em toda tela que exibe um custo. */}
      {aberto && (
        <FluxoRecarga
          direto
          valorInicial={faltamCentavos}
          aoConfirmar={() => {
            if (avisou.current) return;
            avisou.current = true;
            setPago(true);
            aoConfirmar();
          }}
        />
      )}
    </Modal>
  );
}
